import { useEffect, useRef } from "react";
import {
  compact,
  holdCeilings,
  diffMoves,
  type GridLayoutItem,
  type WidgetInstance,
  type WidgetMove,
} from "@salestrak/ui";

import { managedSeed, type ManagedWidget } from "#app/widgets/dashboards/managed-widgets";
import type { WidgetViewStore } from "#app/widgets/dashboards/use-widget-view";
import type { Omittable } from "@salestrak/ui";

/**
 * The managed surface's board maintenance: three ordered effects that keep
 * its active board healthy. (First-visit seeding is NOT
 * one of them — the SERVER lazily seeds a view's default board on its first
 * read, `apps/api/core/src/widgets/widget-view-defaults.ts`.) The host selects
 * the operation channel. Canonical boards use `store.direct.*`; personal-only
 * managed homes use the public personal-mode operations so migrations persist
 * in the viewer's private fork. `canMaintain` keeps dirty staged copies and
 * ordinary personal forks out of canonical maintenance.
 *
 * Order matters: duplicate repair retires extra rows one per pass; the
 * `managedLayoutVersion` migration waits while duplicates exist so it never
 * migrates a snapshot mid-repair; the overlap self-heal waits for BOTH (and
 * for in-flight optimistic adds) before it settles any shared cells.
 */

/** The `widgetKey` of every placed instance that carries one. */
function placedWidgetKeys(widgets: WidgetInstance[]): string[] {
  return widgets.flatMap((widget) => {
    const key = (widget.config as { widgetKey?: unknown } | null)?.widgetKey;
    return typeof key === "string" ? [key] : [];
  });
}

/** True while two placed instances share a `widgetKey` — repair in progress. */
function hasDuplicateKeys(widgets: WidgetInstance[]): boolean {
  const keys = placedWidgetKeys(widgets);
  return new Set(keys).size !== keys.length;
}

/** The catalog's highest baked `managedLayoutVersion` (0 = unversioned). */
function migrationTargetVersion(catalog: ManagedWidget[]): number {
  return catalog.reduce((version, widget) => {
    const candidate = (widget.config as { managedLayoutVersion?: unknown }).managedLayoutVersion;
    return typeof candidate === "number" ? Math.max(version, candidate) : version;
  }, 0);
}

/** True while any placed instance is off-catalog, disabled, retyped, or
 *  behind the catalog's layout revision — the migration effect has work
 *  pending. */
function catalogNeedsMigration(
  catalog: ManagedWidget[],
  widgets: WidgetInstance[],
  targetVersion: number,
): boolean {
  return widgets.some((widget) => {
    const config = widget.config as {
      widgetKey?: string;
      managedLayoutVersion?: number;
    } | null;
    const desired = catalog.find((entry) => entry.widgetKey === config?.widgetKey);
    return (
      !desired ||
      desired.disabled ||
      desired.type !== widget.type ||
      (config?.managedLayoutVersion ?? 0) < targetVersion
    );
  });
}

export function useManagedBoardMaintenance({
  managedCatalog,
  initialLoading,
  canMaintain,
  widgets,
  operations,
}: Omittable<{
  managedCatalog?: ManagedWidget[];
  initialLoading: boolean;
  canMaintain: boolean;
  widgets: WidgetInstance[];
  operations: WidgetViewStore["direct"];
}>) {
  // A managed catalog is a set, keyed by `widgetKey`. Older clients could seed
  // from a stale cached empty response and persist duplicates before the
  // network layout arrived. Keep the first widget for each key and retire any
  // extras so an affected board repairs itself the next time a manager opens it.
  useEffect(() => {
    if (!managedCatalog || initialLoading || !canMaintain) return;
    const catalogKeys = new Set(managedCatalog.map((widget) => widget.widgetKey));
    const seen = new Set<string>();
    const duplicateIds: string[] = [];
    for (const widget of widgets) {
      const key = (widget.config as { widgetKey?: unknown } | null)?.widgetKey;
      if (typeof key !== "string" || !catalogKeys.has(key)) continue;
      if (seen.has(key)) duplicateIds.push(widget.id);
      else seen.add(key);
    }
    // Remove one at a time. The optimistic delete re-renders immediately, then
    // this effect handles the next duplicate without overlapping rollback state.
    const duplicateId = duplicateIds[0];
    if (duplicateId) operations.remove(duplicateId, { compact: false });
  }, [managedCatalog, initialLoading, canMaintain, widgets, operations]);

  // Managed catalogs can intentionally revise their designed home layout. The
  // revision lives in each seed config so the migration is one-time and
  // survives reloads without changing the persisted view key. Existing on/off
  // choices remain intact except newly introduced default widgets; removed
  // and disabled catalog entries are retired, and a widget whose type changed
  // is replaced in the same slot.
  const didMigrateManagedLayoutRef = useRef(false);
  useEffect(() => {
    if (!managedCatalog || initialLoading || !canMaintain || widgets.length === 0) return;
    if (didMigrateManagedLayoutRef.current) return;

    const targetVersion = migrationTargetVersion(managedCatalog);
    if (targetVersion === 0) return;

    // Duplicate repair runs in the preceding effect. Do not migrate the same
    // stale snapshot while one of those duplicate rows is being removed.
    if (hasDuplicateKeys(widgets)) return;

    if (!catalogNeedsMigration(managedCatalog, widgets, targetVersion)) return;
    didMigrateManagedLayoutRef.current = true;

    const catalogByKey = new Map(managedCatalog.map((widget) => [widget.widgetKey, widget]));
    const migratedKeys = new Set<string>();
    const moves: WidgetMove[] = [];

    for (const widget of widgets) {
      const widgetKey = (widget.config as { widgetKey?: string } | null)?.widgetKey;
      const desired = widgetKey ? catalogByKey.get(widgetKey) : undefined;
      if (!desired || desired.disabled || desired.type !== widget.type) {
        operations.remove(widget.id, { compact: false });
        continue;
      }

      migratedKeys.add(desired.widgetKey);
      const seed = managedSeed(desired);
      operations.updateConfig(widget.id, seed.config);
      if (widget.x !== seed.x || widget.y !== seed.y || widget.w !== seed.w || widget.h !== seed.h) {
        moves.push({ id: widget.id, x: seed.x, y: seed.y, w: seed.w, h: seed.h });
      }
    }

    if (moves.length > 0) operations.move(moves);
    const missingDefaults = managedCatalog
      .filter((widget) => widget.defaultOn && !migratedKeys.has(widget.widgetKey))
      .map(managedSeed);
    if (missingDefaults.length > 0) operations.seed(missingDefaults);
  }, [managedCatalog, initialLoading, canMaintain, widgets, operations]);

  // Overlap self-heal: no two visible cards may share a cell. A migration
  // that re-homes designed slots around hidden widgets can land two rows on
  // one rect; settle the overlap downward and persist the correction. Empty
  // space is NOT a defect — a card an editor dragged below a hole stays
  // there, so this pass holds every card at its own row and never floats one
  // up (`holdCeilings` with nothing vacated). A settled layout is a fixed
  // point, so the effect is idempotent. It stands down while ANY other
  // correction is in flight: duplicate rows overlap on purpose until repair
  // retires them, a pending migration is about to issue its own designed-slot
  // moves (settling the same stale snapshot would race that batch), and an
  // optimistic `temp-` add gets its x/y restored from the server on swap-in
  // (a local settle would be silently undone).
  useEffect(() => {
    if (!managedCatalog || initialLoading || !canMaintain || widgets.length === 0) return;
    if (widgets.some((widget) => widget.id.startsWith("temp-"))) return;
    if (hasDuplicateKeys(widgets)) return;
    const targetVersion = migrationTargetVersion(managedCatalog);
    if (targetVersion > 0 && catalogNeedsMigration(managedCatalog, widgets, targetVersion)) {
      return;
    }
    const layout: GridLayoutItem[] = widgets.map((widget) => ({
      id: widget.id,
      x: widget.x,
      y: widget.y,
      w: widget.w,
      h: widget.h,
    }));
    const moves = diffMoves(layout, compact(layout, { ceilings: holdCeilings(layout) }));
    if (moves.length > 0) operations.move(moves);
  }, [managedCatalog, initialLoading, canMaintain, widgets, operations]);
}
